import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import logo from '../../assets/images/The-Key-Logo.png';
import apiClient from "../../utils/api";

function ParkingCarTicket() {
  const { id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [damageMarks, setDamageMarks] = useState([]);

  useEffect(() => {
    const fetchParkingDetails = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await apiClient.get(`accounts/parkings/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const parking = response.data.data;
        setTicket(parking);
        
        // Parse damage marks if present
        const marks = parking.damage_marks ? 
          (typeof parking.damage_marks === 'string' ? 
            JSON.parse(parking.damage_marks) : 
            parking.damage_marks) : 
          [];
        setDamageMarks(marks);

        // Print the ticket after data is loaded
        setTimeout(() => {
          printTicket(parking, marks);
        }, 500);
      } catch (error) {
        console.error('Error loading parking details:', error);
      }
    };

    if (id) {
      fetchParkingDetails();
    }
  }, [id]);

  const printTicket = async (parking, damageMarks) => {
    if (!parking) return;

    // Extract ticket data
    const {
      ticket_number: serial = '',
      plate_number: plate = '',
      model = null,
      color = null,
      parking_time = '',
      parking_date = '',
      parking_type = '',
      has_damage = false,
      parking_location = ''
    } = parking;

    const modelName = model?.name || 'N/A';
    const colorName = color?.name || 'N/A';
    const parkingTypeDisplay = parking_type === 'Complementary' ? 'FREE' : (parking_type || 'Normal');
    const plateDisplay = plate || 'N/A';
    const parkingDateTime = parking_time && parking_date ? 
      `${new Date(parking_date).toLocaleDateString()} ${parking_time}` : 'N/A';
    const serialDisplay = serial || 'N/A';
    const locationDisplay = parking_location || 'N/A';

    // Create ticket content for rawbt printing
    const ticketContent = `
${'-'.repeat(32)}
     THE KEY VALET SERVICES
${'-'.repeat(32)}
PARKING LOCATION: ${locationDisplay}
${'-'.repeat(32)}
Ticket No: ${serialDisplay}
${parkingTypeDisplay}
${'-'.repeat(32)}
      CAR DETAILS
${'-'.repeat(32)}
Plate No.     : ${plateDisplay}
Car Model     : ${modelName}
Color         : ${colorName}
Parking Time  : ${parkingDateTime}
${'-'.repeat(32)}
  Your car is in safe hands
${'-'.repeat(32)}
${parkingTypeDisplay}
${serialDisplay}
${'-'.repeat(32)}
`;

    // Detect user agent for appropriate printing method
    const userAgent = navigator.userAgent.toLowerCase();
    const isAndroid = userAgent.indexOf("android") > -1;
    
    if (isAndroid) {
      // Use Intent scheme for Android devices
      androidPrint(ticketContent);
    } else {
      // Use WebSocket for desktop/PC
      desktopPrint(ticketContent);
    }
  };

  const androidPrint = (data) => {
    try {
      const beforeUrl = 'intent:';
      const afterUrl = '#Intent;scheme=rawbt;package=ru.a402d.rawbtprinter;end;';
      const textEncoded = encodeURI(data);
      window.location.href = beforeUrl + textEncoded + afterUrl;
      console.log('Ticket sent to Android RawBT app via intent scheme');
    } catch (error) {
      console.error('Error sending to Android RawBT app:', error);
      try {
        const base64Data = btoa(unescape(encodeURIComponent(data)));
        const rawbtUrl = `rawbt:data:text/plain;base64,${base64Data}`;
        window.location.href = rawbtUrl;
        console.log('Fallback: Using data scheme with base64');
      } catch (fallbackError) {
        console.error('All methods failed:', fallbackError);
      }
    }
  };

  const desktopPrint = (data) => {
    try {
      const socket = new WebSocket("ws://127.0.0.1:40213/");
      socket.bufferType = "arraybuffer";
      
      socket.onerror = function(error) {
        console.error("WebSocket Error:", error);
      };
      
      socket.onopen = function() {
        socket.send(data);
        socket.close(1000, "Work complete");
        console.log('Ticket sent to desktop RawBT server');
      };
    } catch (error) {
      console.error('Error connecting to desktop RawBT server:', error);
    }
  };

  useEffect(() => {
    if (!ticket) return;

    // Wait for images to load
    const loadImages = async () => {
      // Wait for all images to load
      const images = document.querySelectorAll('.car-diagram img, .logo-section img');
      await Promise.all([...images].map(img => {
        return new Promise((resolve) => {
          if (img.complete) {
            resolve();
          } else {
            img.onload = resolve;
          }
        });
      }));

      // Create a hidden iframe for silent printing
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
      
      // Write the ticket content to the iframe
      iframe.contentWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <style>
              ${document.querySelector('style').innerHTML}
            </style>
          </head>
          <body>
            ${document.querySelector('.print-ticket-container').outerHTML}
          </body>
        </html>
      `);
      
      iframe.contentWindow.document.close();

      // Wait for iframe content to load
      iframe.onload = () => {
        try {
          // Access the iframe's print function
          iframe.contentWindow.print();
          
          // Remove the iframe after printing
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 1000);
        } catch (error) {
          console.error('Print failed:', error);
        }
      };
    };

    loadImages();
  }, [ticket]); // Re-run when ticket changes

  if (!ticket) return null;

  const {
    ticket_number: serial = '',
    plate_number: plate = '',
    model = null,
    color = null,
    parking_time = '',
    parking_date = '',
    parking_type = '',
    parking_location = ''
  } = ticket;

  const modelName = model?.name || 'N/A';
  const colorName = color?.name || 'N/A';
  const parkingTypeDisplay = parking_type === 'Complementary' ? 'FREE' : (parking_type || 'Normal');
  const plateDisplay = plate || 'N/A';
  const parkingDateTime = parking_time && parking_date ? 
    `${new Date(parking_date).toLocaleDateString()} ${parking_time}` : 'N/A';
  const serialDisplay = serial || 'N/A';
  const locationDisplay = parking_location || 'N/A';

  return (
    <div className="print-ticket-container print-only">
      <style>
        {`
        @page {
          margin: 0;
          size: 80mm auto;
        }
        body {
          margin: 0;
          padding: 0;
          background: white;
        }
        .print-ticket-container {
          width: 72mm;
          padding: 2mm;
          font-family: Arial, sans-serif;
          color: #333;
        }
        .ticket-header {
          text-align: center;
          background: #967c3d;
          color: white;
          padding: 2mm;
          border-radius: 1mm;
          margin-bottom: 2mm;
        }
        .ticket-section {
          margin: 2mm 0;
          padding: 2mm;
        }
        .ticket-details {
          margin: 2mm 0;
        }
        .detail-row {
          display: flex;
          justify-content: space-between;
          margin: 1mm 0;
          font-size: 12px;
        }
        .detail-label {
          font-weight: bold;
          min-width: 80px;
        }
        .section-title {
          text-align: center;
          font-weight: bold;
          margin: 2mm 0;
          font-size: 14px;
        }
        .dotted-divider {
          border: none;
          border-top: 2px dotted #FF0000;
          margin: 2mm 0;
        }
        .car-diagram {
          margin: 2mm 0;
          text-align: center;
        }
        .car-diagram h3 {
          font-size: 12px;
          margin: 2mm 0;
        }        .car-damage-view {
          margin: 2mm auto;
          page-break-inside: avoid;
        }
        .damage-checkbox {
          margin: 2mm 0;
          font-size: 10px;
        }
        .checkbox-item {
          display: flex;
          align-items: center;
          margin: 1mm 0;
        }
        .checkbox-item input {
          margin-right: 2mm;
        }
        .logo-section {
          text-align: center;
          margin: 3mm 0;
        }
        .logo-section img {
          width: 40mm;
        }
        .terms {
          margin: 2mm 0;
          font-size: 8px;
          text-align: center;
        }
        .contact {
          text-align: center;
          margin-top: 3mm;
        }
        .contact h2 {
          color: #967c3d;
          margin-bottom: 1mm;
          font-size: 12px;
        }
        .contact-info {
          color: #967c3d;
          font-size: 10px;
        }
        .arabic-text {
          direction: rtl;
          text-align: center;
          margin: 1mm 0;
        }        
        @media print {
          .print-only {
            margin: 0;
            padding: 0;
            width: 72mm;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .ticket-header {
            background: #967c3d !important;
            color: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
        `}
      </style>
      
      <div className="ticket-header">
        THE KEY VALET SERVICES
      </div>

      <hr className="dotted-divider" />
      
      <div className="ticket-section">
        <div className="section-title">PARKING LOCATION</div>
        <div className="detail-row">
          <span>{locationDisplay}</span>
        </div>
      </div>

      <hr className="dotted-divider" />
      
      <div className="ticket-section">
        <div className="detail-row">
          <span className="detail-label">Ticket No:</span>
          <span>{serialDisplay}</span>
        </div>
        <div className="detail-row">
          <span>{parkingTypeDisplay}</span>
        </div>
      </div>

      <hr className="dotted-divider" />

      <div className="ticket-section">
        <div className="section-title">CAR DETAILS</div>
        <div className="ticket-details">
          <div className="detail-row">
            <span className="detail-label">Plate No.:</span>
            <span>{plateDisplay}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Car Model:</span>
            <span>{modelName}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Color:</span>
            <span>{colorName}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Parking Time:</span>
            <span>{parkingDateTime}</span>
          </div>
        </div>
      </div>

      {ticket.has_damage && (
        <div className="car-diagram">
          <div className="car-damage-view" style={{ position: 'relative', maxWidth: '300px', margin: '0 auto' }}>
            <img
              src="/carimage2.png"
              alt="Car Diagram"
              style={{
                width: '100%',
                borderRadius: '10px',
                border: '1px solid #ddd'
              }}
            />          
            {damageMarks.map((dot, index) => {
              const originalWidth = 400;
              const currentImage = document.querySelector('.car-damage-view img');
              const currentWidth = currentImage ? currentImage.offsetWidth : 300;
              const scale = currentWidth / originalWidth;
              const scaledX = dot.x * scale;
              const scaledY = dot.y * scale;

              return (
                <div
                  key={index}
                  style={{
                    position: 'absolute',
                    top: scaledY - 5 + 'px',
                    left: scaledX - 5 + 'px',
                    width: '10px',
                    height: '10px',
                    backgroundColor: '#FF0000',
                    borderRadius: '50%',
                    border: '2px solid #000000'
                  }}
                />
              );
            })}
          </div>
          
          {/* Show damage image if available */}
          {ticket.damage_image_url && (
            <div style={{ marginTop: '10px', textAlign: 'center' }}>
              <h4 style={{ fontSize: '12px', marginBottom: '5px' }}>Damage Image</h4>
              <img 
                src={ticket.damage_image_url} 
                alt="Vehicle Damage" 
                style={{
                  maxWidth: '250px',
                  maxHeight: '150px',
                  border: '1px solid #ddd',
                  borderRadius: '5px'
                }}
              />
            </div>
          )}
        </div>
      )}

      <div className="damage-checkbox">
        <div className="checkbox-item">
          <input 
            type="checkbox" 
            id="no-damage" 
            checked={!ticket.has_damage}
            readOnly 
          />
          <label htmlFor="no-damage">There is no damage.</label>
        </div>
        <div className="checkbox-item">
          <input 
            type="checkbox" 
            id="has-damage" 
            checked={ticket.has_damage}
            readOnly 
          />
          <label htmlFor="has-damage">There are damages.</label>
        </div>
      </div>

      <hr className="dotted-divider" />

      <div className="logo-section">
        <img src={logo} alt="The Key Valet" />
      </div>

      <div className="terms">
        <p>
          Any individual operating the car represents
          the driver and the owner of the car.
          Please report any damage to the car before handling it.
          The company is not responsible for any damage
          caused by natural catastrophes, theft, accidents,
          or mechanical failure.
          No employee has the authority to modify these terms.
        </p>
      </div>

      <hr className="dotted-divider" />

      <div className="contact">
        <h2>CONTACT US</h2>
        <div className="contact-info">
          <div>INFO@THEKEYVALET.COM</div>
          <div>+974 3377 7441</div>
        </div>
      </div>
    </div>
  );
}

export default ParkingCarTicket;